// Deep Space /summarize card — the "star-map recap". One card that morphs in place: while the recap
// is generated it shows an animated space scene (data-core orb + orbiting signal bubbles, a satellite
// fly-by, and an alien mascot) with a title + shimmering skeleton; when the summary arrives it settles
// into a star-chart emblem + the recap unfurls. Deep-space theme only (gated in MessageList); scales
// as one 660px unit via CSS zoom like the other deep-space cards.

import { useEffect, useRef, useState } from "react";
import SpaceSky from "./SpaceSky.jsx";
import "../deep-space-summary.css";

const LBL = { day: "the last day", week: "the last week", month: "the last month" };

// The loader scene art (ring, data-core orb + scan, probe fly-by, orbiting signal bubbles, alien
// mascot) — lifted from the design prototype; only the mascot path is repointed. The seeded starfield
// behind it is the real <SpaceSky> React component (rendered as a sibling), so it's not in this string.
const SCENE_ART = `
<svg viewBox="0 0 200 200" width="210" height="210" style="position:absolute; right:26px; top:6px; opacity:.5; animation:dsm-ringspin 26s linear infinite;">
  <circle cx="100" cy="100" r="92" fill="none" stroke="#2f6f8f" stroke-width="1.2" stroke-dasharray="2 12"/>
  <circle cx="100" cy="100" r="78" fill="none" stroke="#265a74" stroke-width="1" stroke-dasharray="16 10"/>
  <g fill="#7fe9df" opacity=".8"><circle cx="100" cy="8" r="2.4"/><circle cx="192" cy="100" r="2.4"/><circle cx="100" cy="192" r="2.4"/><circle cx="8" cy="100" r="2.4"/></g>
</svg>
<div style="position:absolute; right:174px; bottom:82px; width:94px; height:94px; border-radius:50%; overflow:hidden; z-index:5; background:radial-gradient(circle at 38% 30%,rgba(200,240,255,.6),rgba(90,160,220,.5) 42%,rgba(40,90,120,.6) 64%,rgba(10,25,40,.92)); border:1px solid rgba(120,200,255,.5); animation:dsm-orbpulse 2.6s ease-in-out infinite;">
  <div style="position:absolute; left:22%; top:16%; width:26%; height:20%; border-radius:50%; background:radial-gradient(circle,rgba(220,245,255,.9),transparent 70%); filter:blur(1px);"></div>
  <div style="position:absolute; left:0; right:0; top:0; height:4px; background:linear-gradient(90deg,transparent,#7fe9df,transparent); filter:drop-shadow(0 0 6px #35e0d0); animation:dsm-scan 2.2s ease-in-out infinite;"></div>
</div>
<div style="position:absolute; left:0; bottom:96px; width:70px; height:44px; z-index:6; animation:dsm-satfly 9s linear infinite;">
  <svg viewBox="0 0 70 44" width="70" height="44" style="overflow:visible;">
    <rect x="8" y="12" width="20" height="12" rx="2" fill="#2b4a7a" stroke="#5fa8ff" stroke-width=".8"></rect>
    <rect x="42" y="12" width="20" height="12" rx="2" fill="#2b4a7a" stroke="#5fa8ff" stroke-width=".8"></rect>
    <rect x="28" y="8" width="14" height="20" rx="3" fill="#dfe9f4" stroke="#9fb4cc" stroke-width=".8"></rect>
    <circle cx="35" cy="18" r="3.4" fill="#35e0d0"></circle>
  </svg>
</div>
<div style="position:absolute; right:205px; bottom:129px; animation:dsm-orbit 8s linear infinite;">
  <div style="position:relative; width:40px; height:27px; border-radius:9px; background:linear-gradient(155deg,rgba(160,230,255,.95),rgba(53,224,208,.9)); border:1px solid rgba(205,240,255,.55); box-shadow:0 4px 9px rgba(0,0,0,.4); display:flex; align-items:center; justify-content:center; gap:4px;"><span style="width:4px; height:4px; border-radius:50%; background:#fff;"></span><span style="width:4px; height:4px; border-radius:50%; background:#fff;"></span><span style="width:4px; height:4px; border-radius:50%; background:#fff;"></span></div>
</div>
<div style="position:absolute; right:205px; bottom:129px; animation:dsm-orbit 8s linear -2.667s infinite;">
  <div style="position:relative; width:38px; height:26px; border-radius:9px; background:linear-gradient(155deg,rgba(255,210,150,.92),rgba(255,180,84,.85)); border:1px solid rgba(255,230,200,.5); box-shadow:0 4px 9px rgba(0,0,0,.4); display:flex; align-items:center; justify-content:center; gap:4px;"><span style="width:3.6px; height:3.6px; border-radius:50%; background:#fff;"></span><span style="width:3.6px; height:3.6px; border-radius:50%; background:#fff;"></span><span style="width:3.6px; height:3.6px; border-radius:50%; background:#fff;"></span></div>
</div>
<div style="position:absolute; right:205px; bottom:129px; animation:dsm-orbit 8s linear -5.333s infinite;">
  <div style="position:relative; width:36px; height:25px; border-radius:9px; background:linear-gradient(155deg,rgba(150,240,200,.9),rgba(79,209,138,.85)); border:1px solid rgba(210,255,235,.5); box-shadow:0 4px 9px rgba(0,0,0,.4); display:flex; align-items:center; justify-content:center; gap:4px;"><span style="width:3.6px; height:3.6px; border-radius:50%; background:#fff;"></span><span style="width:3.6px; height:3.6px; border-radius:50%; background:#fff;"></span><span style="width:3.6px; height:3.6px; border-radius:50%; background:#fff;"></span></div>
</div>
<div style="position:absolute; right:20px; bottom:86px; width:34%; z-index:7; animation:dsm-bob2 .8s ease-in-out infinite;">
  <img src="/deepspace/yoda_PNG15.png" alt="little visitor" style="width:100%; height:auto; display:block; filter:brightness(1) contrast(1.06) drop-shadow(0 3px 5px rgba(0,0,0,.55));"/>
</div>`;

export default function DeepSpaceSummaryCard({ phase = "loading", period = "week", channelName = "", time = "", onRerun, children }) {
  const fitRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const lbl = LBL[period] || LBL.week;

  // Scale the whole 660px card as one unit to fit its container (CSS zoom → layout-aware, keeps height).
  useEffect(() => {
    const wrap = fitRef.current;
    if (!wrap) return;
    const apply = () => { const w = wrap.clientWidth; if (w) setZoom(Math.min(1, w / 660)); };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={fitRef} className="dsm-fit">
      <div className="dsm-card" style={{ zoom }}>
        <div className="dsm-card__inner">
          <div className="dsm-badge">
            <span className="dsm-badge__pill">/summarize</span>
            {channelName ? <span className="dsm-badge__chan">{channelName}</span> : null}
            <span className="dsm-badge__private">🔒 private to you</span>
          </div>

          {phase === "loading" && (
            <>
              <div className="dsm-scene" aria-hidden>
                <SpaceSky planet={false} moon={false} shooting starCount={45} seed={321} />
                <div className="dsm-scene__art" dangerouslySetInnerHTML={{ __html: SCENE_ART }} />
              </div>
              <div className="dsm-copy">
                <div className="dsm-title dsm-title--load">Scanning<br />the logs…</div>
                <div className="dsm-status">{lbl} · downloading telemetry</div>
                <div className="dsm-skel"><span /><span /><span /></div>
              </div>
            </>
          )}

          {phase === "error" && (
            <div className="dsm-err">
              <div className="dsm-title">Signal lost</div>
              <div className="dsm-status">Run /summarize again to re-scan the logs.</div>
            </div>
          )}

          {phase === "done" && (
            <div className="dsm-done">
              <div className="dsm-donehead">
                <div>
                  <div className="dsm-title">Chat summary</div>
                  <div className="dsm-meta">{lbl}{channelName ? ` · ${channelName}` : ""} · only you can see this</div>
                </div>
                <div className="dsm-emblem" aria-hidden><span>&#10022;</span></div>
              </div>
              <div className="dsm-recap">{children}</div>
              <div className="dsm-foot">
                <span className="dsm-foot__lbl">🔒 {time || "scanned just now"} · private to you</span>
                {onRerun ? (
                  <button type="button" className="dsm-again" onClick={() => onRerun(period)}>↻ scan again</button>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
